import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useOptionalAuth } from '../../../context/AuthContext';
import { getThemeDefaultSections, useTenant } from '../../../context/TenantContext';
import type { MarketingSectionId } from '../../../context/TenantContext';
import { ANNOUNCE, FOOTER_LINKS, LEGAL } from './content';
import { REVISE_ANCHORS, REVISE_IEB_NAV, REVISE_NAV } from './sectionMeta';
import { useAnchorHref } from './links';

/** The tenant's active section list, so chrome can't link to a section that
 *  isn't on the page. */
const useActiveSections = (): ReadonlyArray<MarketingSectionId> => {
  const { tenant } = useTenant();
  return tenant?.branding.marketing?.sections ?? getThemeDefaultSections('revise');
};

/**
 * True once the page has scrolled past the masthead's own height.
 *
 * The threshold is the nav height rather than a few pixels: the bar only needs
 * its backing once content is actually passing underneath it, and switching at
 * ~10px makes it flicker while someone nudges the page. Reads are
 * rAF-throttled and the state is set through a comparison so an unchanged
 * value doesn't re-render on every scroll event — the same shape PublicNavbar
 * uses.
 */
const useScrolledPast = (threshold = 72): boolean => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setScrolled((prev) => {
          const next = window.scrollY > threshold;
          return prev === next ? prev : next;
        });
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [threshold]);
  return scrolled;
};

/**
 * The bar above the nav. It carries the offer, so it is a link into the free
 * section rather than a dismissible notice — this is the single most important
 * sentence on the page and it should still be reachable at the bottom.
 */
export const ReviseAnnounceBar: React.FC = () => {
  const { tenant } = useTenant();
  const sections = useActiveSections();
  const anchorHref = useAnchorHref();
  const announce = tenant?.branding.marketing?.announce ?? ANNOUNCE;

  const text = announce.text;
  if (!text) return null;

  const configured = announce.href ?? `#${REVISE_ANCHORS.labPractice}`;
  const isAnchor = configured.startsWith('#');
  // An anchor into a section the tenant has removed would scroll nowhere, so
  // the bar keeps its message and loses the link rather than dead-ending.
  // Live if the anchor names a rendered section, or a nav target inside one
  // (the demo's `#try` lives inside labStudy rather than being a section).
  const target = configured.slice(1);
  const anchorIsLive =
    !isAnchor ||
    sections.some((id) => REVISE_ANCHORS[id] === target) ||
    REVISE_NAV.some((l) => l.anchor === target && sections.includes(l.id));
  const href = isAnchor ? anchorHref(configured.slice(1)) : configured;

  const body = (
    <>
      {announce.badge && <span className="rv-freebar-badge">{announce.badge}</span>}
      <span>
        {text}
        {anchorIsLive && (
          <>
            {' '}
            <span aria-hidden="true">&rarr;</span>
          </>
        )}
      </span>
    </>
  );

  if (!anchorIsLive) return <div className="rv-freebar">{body}</div>;
  return isAnchor ? (
    <a className="rv-freebar" href={href}>
      {body}
    </a>
  ) : (
    <Link className="rv-freebar" to={href}>
      {body}
    </Link>
  );
};

/**
 * Masthead. The nav links are derived from the ACTIVE section list rather than
 * hardcoded, so removing a section can never leave an anchor pointing at
 * nothing.
 */
export const ReviseNavbar: React.FC = () => {
  const { tenant } = useTenant();
  const auth = useOptionalAuth();
  const sections = useActiveSections();
  const anchorHref = useAnchorHref();
  const scrolled = useScrolledPast();
  const { pathname } = useLocation();
  const isAuthenticated = auth?.isAuthenticated ?? false;

  const name = tenant?.name ?? 'Kula Revise';
  // "Kula Revise Grade 12 History" — first word pink, the rest ink. Falls back
  // to the whole name when there is only one word.
  const [first, ...rest] = name.split(' ');

  /* Which board's masthead this is. The nav's section links are anchors into
     whichever page owns them, so on `/ieb` the NSC set pointed every one of
     them back at the landing page — an IEB student clicking "Study Labs" left
     the board they had just chosen. Each board now lists its own sections and
     offers one labelled door to the other. */
  const onIeb = pathname === '/ieb';
  const links: ReadonlyArray<{ anchor: string; label: string }> = onIeb
    ? REVISE_IEB_NAV
    : REVISE_NAV.filter((l) => sections.includes(l.id));
  const otherBoard = onIeb ? { to: '/', label: 'NSC' } : { to: '/ieb', label: 'IEB' };

  return (
    <nav className="rv-nav" data-scrolled={scrolled ? 'true' : 'false'}>
      <div className="rv-wrap">
        <Link className="rv-logo" to="/">
          <span className="rv-logo-k">{first}</span>
          {rest.length > 0 && ` ${rest.join(' ')}`}
        </Link>
        <div className="rv-navlinks">
          {links.map((l) => (
            <a key={l.anchor} href={onIeb ? `#${l.anchor}` : anchorHref(l.anchor)}>
              {l.label}
            </a>
          ))}
          {/* A route, not an anchor, so it lives outside either nav list: the
              boards are pages, and a reader scanning the masthead should find
              theirs without reading the hero. */}
          <Link to={otherBoard.to}>{otherBoard.label}</Link>
          <Link to={isAuthenticated ? '/browse' : '/login'}>
            {isAuthenticated ? 'My learning' : 'Sign in'}
          </Link>
        </div>
      </div>
    </nav>
  );
};

/**
 * Footer. Carries the two provenance statements rather than a colophon: what
 * is departmental material and what is ours is the kind of thing a teacher
 * checks before recommending this to a class.
 */
export const ReviseFooter: React.FC = () => {
  const { tenant } = useTenant();
  const anchorHref = useAnchorHref();
  const legal = tenant?.branding.marketing?.legal?.length
    ? tenant.branding.marketing.legal
    : LEGAL;
  const links = tenant?.branding.marketing?.footerLinks?.length
    ? tenant.branding.marketing.footerLinks
    : FOOTER_LINKS;

  return (
    <footer className="rv-footer">
      <div className="rv-wrap">
        {legal.map((para) => (
          <p key={para.slice(0, 40)}>{para}</p>
        ))}
        <div className="rv-footer-links">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          {links.map((l) =>
            /^https?:\/\//.test(l.href) ? (
              <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer">
                {l.label}
              </a>
            ) : l.href.startsWith('#') ? (
              // Root-qualified for the same reason the nav is: the footer
              // renders on /courses and /privacy too, where a bare `#who`
              // targets the current page and scrolls nowhere.
              <a key={l.href} href={anchorHref(l.href.slice(1))}>
                {l.label}
              </a>
            ) : (
              <Link key={l.href} to={l.href}>
                {l.label}
              </Link>
            ),
          )}
        </div>
      </div>
    </footer>
  );
};
